import { useMemo, useState } from 'react';

const documentTypes = [
  { type: 'PERMIT', label: 'پروانه ساخت', hint: 'پروانه، مجوز شروع یا تمدید ساخت' },
  { type: 'OWNERSHIP', label: 'اسناد مالکیت', hint: 'سند، قرارداد مالکیت یا مدارک زمین' },
  { type: 'CONTRACT', label: 'قراردادها', hint: 'قرارداد مشارکت، پیمانکاری یا سرمایه گذاری' },
  { type: 'CERTIFICATE', label: 'گواهی ها', hint: 'گواهی پایان کار یا تاییدیه های رسمی' },
  { type: 'IMAGE', label: 'تصاویر پروژه', hint: 'عکس های نمای پروژه، کارگاه یا پیشرفت ساخت', accept: 'image/*' },
  { type: 'OTHER', label: 'سایر مدارک', hint: 'هر فایل تکمیلی مرتبط با پروژه' }
];

function typeLabel(type) {
  return documentTypes.find((item) => item.type === type)?.label || type;
}

function isImageDoc(doc) {
  const url = doc.file_url || doc.previewUrl || '';
  return doc.type === 'IMAGE' || /\.(png|jpe?g|webp|gif|bmp)$/i.test(url.split('?')[0]);
}

function DocumentUploadBox({ busy, definition, docs, onUpload }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [localImages, setLocalImages] = useState([]);

  const uploadFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setError('');
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('type', definition.type);
        fd.append('file', file);
        const uploaded = await onUpload(fd);
        if (!uploaded) throw new Error('Upload failed');
        if (definition.type === 'IMAGE') {
          setLocalImages((current) => [
            {
              id: uploaded?.id || `${file.name}-${file.lastModified}`,
              type: 'IMAGE',
              file_url: uploaded?.file_url,
              previewUrl: uploaded?.file_url || URL.createObjectURL(file),
              name: file.name
            },
            ...current
          ]);
        }
      }
      event.target.value = '';
    } catch (err) {
      setError('خطا در بارگذاری فایل.');
    } finally {
      setUploading(false);
    }
  };

  const imageDocs = definition.type === 'IMAGE'
    ? [...localImages, ...docs].filter((doc, index, list) => list.findIndex((item) => item.id === doc.id) === index)
    : [];

  return (
    <section className={`dev-doc-box ${docs.length ? 'complete' : ''}`}>
      <div className="dev-doc-box-head">
        <div>
          <h3>{definition.label}</h3>
          <p>{definition.hint}</p>
        </div>
        <span>{docs.length}</span>
      </div>
      <label className="dev-doc-drop">
        <input
          type="file"
          multiple
          accept={definition.accept}
          disabled={busy || uploading}
          onChange={uploadFiles}
        />
        <strong>{uploading ? 'در حال بارگذاری...' : 'انتخاب فایل'}</strong>
        <small>امکان انتخاب چند فایل</small>
      </label>
      {error && <div className="dev-alert error compact">{error}</div>}
      {definition.type === 'IMAGE' && imageDocs.length > 0 && (
        <div className="dev-doc-images">
          {imageDocs.filter(isImageDoc).map((doc) => (
            <a key={doc.id} href={doc.file_url || doc.previewUrl} target="_blank" rel="noreferrer">
              <img src={doc.previewUrl || doc.file_url} alt={doc.name || definition.label} />
            </a>
          ))}
        </div>
      )}
      {docs.length > 0 && (
        <div className="dev-doc-mini-list">
          {docs.slice(0, 4).map((doc) => (
            <a key={doc.id} href={doc.file_url} target="_blank" rel="noreferrer">
              <span>{typeLabel(doc.type)}</span>
              <small>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('fa-IR') : 'فایل ثبت شده'}</small>
            </a>
          ))}
          {docs.length > 4 && <small className="dev-muted">+{docs.length - 4} فایل دیگر</small>}
        </div>
      )}
    </section>
  );
}

export default function ProjectDocumentsStep({ busy, selectedProject, documents, onUpload }) {
  const documentsByType = useMemo(() => {
    const grouped = Object.fromEntries(documentTypes.map((item) => [item.type, []]));
    documents.forEach((doc) => {
      if (!grouped[doc.type]) grouped[doc.type] = [];
      grouped[doc.type].push(doc);
    });
    return grouped;
  }, [documents]);

  if (!selectedProject) {
    return (
      <div className="dev-panel wide">
        <h2>مدارک پروژه</h2>
        <p className="dev-muted">ابتدا اطلاعات پروژه را ذخیره کنید.</p>
      </div>
    );
  }

  return (
    <div className="dev-documents-step">
      <div className="dev-panel wide dev-documents-head">
        <div>
          <h2>مدارک پروژه</h2>
          <p>برای هر نوع مدرک، فایل های همان بخش را انتخاب کنید تا چیزی از قلم نیفتد.</p>
        </div>
        <strong>{documents.length}</strong>
      </div>
      <div className="dev-doc-box-grid">
        {documentTypes.map((definition) => (
          <DocumentUploadBox
            key={definition.type}
            busy={busy}
            definition={definition}
            docs={documentsByType[definition.type] || []}
            onUpload={onUpload}
          />
        ))}
      </div>
    </div>
  );
}
